import * as THREE from 'three';

/**
 * Noise and small textures made in code (ADR-032): the water's ripples, the
 * halos' and particles' soft discs, the smoke. `DataTexture`s rather than a
 * canvas, so they build in Node for the tests and cost no download.
 */

/** A small, seeded generator: the same world every time, on every headset. */
export function random(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    // mulberry32
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smooth value noise on a lattice of `period` cells that wraps, in 0–1. */
export function valueNoise(seed: number, period = 256): (x: number, y: number) => number {
  const next = random(seed);
  const lattice = Float32Array.from({ length: period * period }, () => next());
  const at = (i: number, j: number): number =>
    lattice[(((j % period) + period) % period) * period + (((i % period) + period) % period)]!;
  return (x, y) => {
    const i = Math.floor(x);
    const j = Math.floor(y);
    const u = x - i;
    const v = y - j;
    const su = u * u * (3 - 2 * u);
    const sv = v * v * (3 - 2 * v);
    const a = at(i, j) + (at(i + 1, j) - at(i, j)) * su;
    const b = at(i, j + 1) + (at(i + 1, j + 1) - at(i, j + 1)) * su;
    return a + (b - a) * sv;
  };
}

/** A few octaves of value noise, in 0–1. */
export function fbm(seed: number, octaves = 4, period = 256): (x: number, y: number) => number {
  const noise = valueNoise(seed, period);
  return (x, y) => {
    let sum = 0;
    let amplitude = 0.5;
    let frequency = 1;
    let total = 0;
    for (let o = 0; o < octaves; o++) {
      sum += noise(x * frequency + o * 17.3, y * frequency - o * 9.1) * amplitude;
      total += amplitude;
      amplitude *= 0.5;
      frequency *= 2;
    }
    return sum / total;
  };
}

/** A tiling normal map of soft ripples, `size`², for the water's detail. */
export function rippleNormals(size = 128, seed = 7): THREE.DataTexture {
  const cells = 8;
  const noise = fbm(seed, 3, cells);
  const height = (x: number, y: number): number => noise((x / size) * cells, (y / size) * cells);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (height(x + 1, y) - height(x - 1, y)) * 6;
      const dy = (height(x, y + 1) - height(x, y - 1)) * 6;
      const n = new THREE.Vector3(-dx, -dy, 1).normalize();
      data.set(
        [(n.x * 0.5 + 0.5) * 255, (n.y * 0.5 + 0.5) * 255, (n.z * 0.5 + 0.5) * 255, 255],
        (y * size + x) * 4,
      );
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

/** A white disc fading softly to nothing: halos, dust, ash. */
export function softDisc(size = 64, falloff = 2.2): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const r = Math.hypot((x + 0.5) / size - 0.5, (y + 0.5) / size - 0.5) * 2;
      const a = r >= 1 ? 0 : Math.exp(-falloff * falloff * r * r) * (1 - r * r);
      data.set([255, 255, 255, Math.round(a * 255)], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

/** A soft, lumpy puff: smoke and steam. */
export function puff(size = 64, seed = 3): THREE.DataTexture {
  const noise = fbm(seed, 4, 8);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      const v = (y + 0.5) / size;
      const r = Math.hypot(u - 0.5, v - 0.5) * 2;
      const lump = noise(u * 4, v * 4);
      const edge = Math.max(0, 1 - r * r) * (0.55 + 0.9 * lump);
      const a = Math.min(1, Math.max(0, edge - 0.15) * 1.3);
      const shade = 200 + 55 * lump;
      data.set([shade, shade, shade, Math.round(a * 255)], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}
